import React from 'react';
import { Trash, ChevronDown, ChevronRight, Eye, EyeOff, MoveRight, Plus } from 'lucide-react';
// 从SlideList导入类型定义，避免重复定义
import type { Slide, SlideVersion } from './SlideList';

type Props = {
  slide: Slide;
  expandedSlideId: number | null;
  toggleSlideExpansion: (slideId: number) => void;
  onEnter: (mode: 'ware', id: number) => void;
  onDeleteSlideVersion: (slideId: number, versionId: number) => void;
  canManage: boolean;
  isDisabled: boolean;
  currentUserId: number;
  currentUserRole: string;
  newIndexes: Record<number, string>;
  handleNewIndexChange: (slideId: number, value: string) => void;
  handleMoveSlide: (slide: Slide) => void;
  movingSlideId: number | null;
  handleCreateNewVersion: (slide: Slide) => void;
  handleDisableSlide: (slide: Slide) => void;
  handleEnableSlide: (slide: Slide) => void;
};

export const SlideItem: React.FC<Props> = ({
  slide,
  expandedSlideId,
  toggleSlideExpansion,
  onEnter,
  onDeleteSlideVersion,
  canManage,
  isDisabled,
  currentUserId,
  currentUserRole,
  newIndexes,
  handleNewIndexChange,
  handleMoveSlide,
  movingSlideId,
  handleCreateNewVersion,
  handleDisableSlide,
  handleEnableSlide
}) => {
  return (
    <div key={slide.id} className="py-2">
      {/* 一级slide对象 - 可伸缩 */}
      <div 
        className={`flex items-center justify-between p-3 rounded-md hover:bg-gray-100 transition-colors cursor-pointer ${isDisabled ? 'bg-gray-200 opacity-75' : 'bg-gray-50'}`} 
        onClick={() => toggleSlideExpansion(slide.id)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold ${isDisabled ? 'bg-gray-300 text-gray-600' : 'bg-purple-100 text-purple-600'}`}>
            P{slide.slide_index}
          </div>
          <div className={`text-sm font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-800'}`}>
            幻灯片 {slide.slide_index}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* 移动幻灯片功能 - 仅未禁用的幻灯片可用 */}
          {canManage && (
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="999"
                value={newIndexes[slide.id] || slide.slide_index}
                onChange={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡
                  handleNewIndexChange(slide.id, e.target.value);
                }}
                className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()}
                placeholder={slide.slide_index.toString()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡
                  handleMoveSlide(slide);
                }}
                className={`p-1 text-xs flex items-center gap-1 rounded-md transition-colors ${movingSlideId === slide.id
                  ? 'bg-blue-100 text-blue-600 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'}`}
                title="移动幻灯片"
                disabled={movingSlideId === slide.id}
              >
                <MoveRight size={14} />
                移动
              </button>
            </div>
          )}
          
          <div className="text-xs text-gray-500">
            最新版本: {slide.latest_version} {/* 移除版本号前的v */}
          </div>
          
          {/* 最高版本is_final状态显示 */}
          {(() => {
            const latestVersion = slide.versions.find(
              (version) => version.version_number === slide.latest_version
            );
            if (!latestVersion) return null;
            
            return (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${latestVersion.is_final
                ? 'bg-green-100 text-green-800'  // 绿色定稿
                : 'bg-yellow-100 text-yellow-800'  // 黄色含草稿
              }`}>
                {latestVersion.is_final ? '定稿' : '含草稿'}
              </span>
            );
          })()}
          
          {/* 启用/禁用按钮 - 只有有权限的用户才能看到 */}
          {canManage && (
            <div className="flex items-center gap-2">
              {!isDisabled ? (
                <button 
                  onClick={(e) => {
                    e.stopPropagation(); // 阻止事件冒泡
                    handleDisableSlide(slide);
                  }} 
                  className="p-1 text-gray-500 hover:text-red-600" 
                  title="禁用幻灯片"
                >
                  <EyeOff size={16} />
                </button>
              ) : (
                <button 
                  onClick={(e) => {
                    e.stopPropagation(); // 阻止事件冒泡
                    handleEnableSlide(slide);
                  }} 
                  className="p-1 text-gray-500 hover:text-green-600" 
                  title="启用幻灯片"
                >
                  <Eye size={16} />
                </button>
              )}
            </div>
          )}
          
          {/* 新增版本按钮 - 不受任何限制，任何人任何时候都可使用 */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // 阻止事件冒泡
              handleCreateNewVersion(slide);
            }}
            className="p-1 text-xs flex items-center gap-1 rounded-md bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600 transition-colors"
            title="新增版本"
          >
            <Plus size={14} />
            新增版本
          </button>
          
          {/* 更多按钮 - 展开/折叠指示器 */}
          <button className="p-1 text-gray-500 hover:text-gray-700">
            {expandedSlideId === slide.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
      </div>

      {/* 二级slide版本列表 - 可伸缩面板 */}
      {expandedSlideId === slide.id && (
        <div className="ml-11 mt-2 space-y-2 pb-2">
          {/* 按版本号降序排列 */}
          {[...slide.versions].sort((a, b) => {
            // 处理版本号带有"v"前缀的情况
            const cleanVersionA = a.version_number.replace(/^v/, '');
            const cleanVersionB = b.version_number.replace(/^v/, '');
            
            // 版本号比较函数，按降序排列
            const aParts = cleanVersionA.split('.').map(Number);
            const bParts = cleanVersionB.split('.').map(Number);
            
            for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
              const aVal = aParts[i] || 0;
              const bVal = bParts[i] || 0;
              if (aVal !== bVal) {
                return bVal - aVal; // 降序排列
              }
            }
            return 0;
          }).map(version => (
            <div key={version.id} className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="text-xs font-medium text-purple-600">{version.version_number} {/* 移除版本号前的v */}</div>
                <div className="text-xs text-gray-500">{version.created_by_username}</div>
                <div className="text-xs text-gray-400">{new Date(version.updated_at).toLocaleString()}</div>
                {/* is_final状态显示 */}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${version.is_final
                  ? 'bg-green-100 text-green-800'  // 绿色定稿
                  : 'bg-yellow-100 text-yellow-800'  // 黄色草稿中
                }`}>
                  {version.is_final ? '定稿' : '草稿中'}
                </span>
                {/* 删除按钮 - sys角色可删除任何版本；普通用户只能删除自己创建的草稿版本 */}
                {(currentUserRole === 'sys' || (version.created_by === currentUserId && !version.is_final)) && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSlideVersion(slide.id, version.id);
                    }} 
                    className="text-xs text-red-600 hover:text-red-800"
                    title={`删除版本 ID: ${version.id}`}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* view按钮 - 跳转到WorkshopPage页面 */}
                <button 
                  onClick={(e) => {
                    e.stopPropagation(); // 阻止事件冒泡，避免触发slide展开/折叠
                    onEnter('ware', version.id); // 使用正确的导航函数，传递幻灯片版本ID
                  }} 
                  className="text-xs text-blue-600 hover:underline"
                >
                  view
                </button>
                
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
